import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Layout/Header';
import { salaryAPI, employeeAPI } from '../lib/api';
import { formatCurrency, getMonthOptions, getCurrentMonth, formatMonth, downloadBlob } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Plus, Download, FileSpreadsheet, Send, ArrowRight,
  Users, Wallet, RefreshCw, Eye, FileText, Trash2, Check, AlertTriangle, Banknote, Search,
  Edit2, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Import extracted components
import {
  SalaryStatusSummary,
  SalarySummaryCards,
  SalaryFormModal,
  SalaryDetailModal,
  SalaryAuditModal,
  SalaryTableRow,
  GenerateBatchModal,
  EditBatchRecordModal,
  RejectBatchModal,
  BatchPayslipModal
} from '../components/salary';

const SalaryManagement = () => {
  const { user } = useAuth();
  const [salaryRecords, setSalaryRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [selectedDetailRecord, setSelectedDetailRecord] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [formData, setFormData] = useState({
    employee_id: '',
    month: getCurrentMonth(),
    basic_pay: 0, hra: 0, da: 0, special_allowance: 0, conveyance: 0, medical_allowance: 0,
    other_allowances: 0, overtime: 0, bonus: 0, arrears: 0,
    pf_deduction: 0, esi_deduction: 0, tds_deduction: 0, professional_tax: 0,
    leave_deduction: 0, loan_deduction: 0, advance_deduction: 0, other_deductions: 0,
    remarks: '',
  });

  // Main tab state
  const [mainTab, setMainTab] = useState('individual');
  
  // Bulk Salary State
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [batchRecords, setBatchRecords] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [bulkMonth, setBulkMonth] = useState(new Date().getMonth() + 1);
  const [bulkYear, setBulkYear] = useState(new Date().getFullYear());
  const [editBatchModalOpen, setEditBatchModalOpen] = useState(false);
  const [editingBatchRecord, setEditingBatchRecord] = useState(null);
  const [editBatchForm, setEditBatchForm] = useState({});
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [payslipModalOpen, setPayslipModalOpen] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  const monthOptions = getMonthOptions(12);
  const isAdmin = user?.role === 'admin';
  const isHR = user?.role === 'hr';
  const isVerifier = user?.role === 'verifier';
  
  const canEditSalary = isVerifier || isHR;
  const canApproveSalary = isAdmin;
  const canGenerate = isVerifier || isHR;
  const canAuthorize = isAdmin;

  const bulkMonths = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' },
  ];
  const bulkYears = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  useEffect(() => {
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth]);

  useEffect(() => {
    if (mainTab === 'bulk') {
      fetchBatches();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab]);

  const fetchData = async () => {
    try {
      const [salaryRes, empRes] = await Promise.all([
        salaryAPI.getAll({ month: selectedMonth }),
        employeeAPI.getAll({ status: 'active' }),
      ]);
      setSalaryRecords(salaryRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  // Bulk Salary Functions
  const fetchBatches = async () => {
    setLoadingBatches(true);
    try {
      const res = await salaryAPI.getBatches();
      setBatches(res.data);
    } catch (error) {
      toast.error('Failed to fetch salary batches');
    } finally {
      setLoadingBatches(false);
    }
  };

  const fetchBatchDetails = async (batchId) => {
    setLoadingRecords(true);
    try {
      const res = await salaryAPI.getBatch(batchId);
      setSelectedBatch(res.data);
      setBatchRecords(res.data.records || []);
    } catch (error) {
      toast.error('Failed to fetch batch details');
    } finally {
      setLoadingRecords(false);
    }
  };

  const handleGenerateBatch = async () => {
    setGenerating(true);
    try {
      const res = await salaryAPI.generateBatch(bulkMonth, bulkYear);
      toast.success(res.data.message);
      setGenerateModalOpen(false);
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate salary batch');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmitBatchForAuthorization = async () => {
    if (!selectedBatch) return;
    try {
      await salaryAPI.submitBatchForVerification(selectedBatch.id);
      toast.success('Batch submitted for authorization');
      fetchBatchDetails(selectedBatch.id);
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit batch');
    }
  };

  const handleAuthorizeBatch = async () => {
    if (!selectedBatch) return;
    try {
      await salaryAPI.authorizeBatch(selectedBatch.id);
      toast.success('Batch authorized successfully! Employees have been notified.');
      fetchBatchDetails(selectedBatch.id);
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to authorize batch');
    }
  };

  const handleRejectBatch = async () => {
    if (!selectedBatch) return;
    try {
      await salaryAPI.rejectBatch(selectedBatch.id, rejectReason);
      toast.success('Batch rejected and sent back for revision');
      setRejectModalOpen(false);
      setRejectReason('');
      fetchBatchDetails(selectedBatch.id);
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject batch');
    }
  };

  const handleDeleteBatch = async () => {
    if (!selectedBatch) return;
    if (!window.confirm('Are you sure you want to delete this batch? This cannot be undone.')) return;
    try {
      await salaryAPI.deleteBatch(selectedBatch.id);
      toast.success('Batch deleted');
      setSelectedBatch(null);
      setBatchRecords([]);
      fetchBatches();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete batch');
    }
  };

  const handleDownloadBatchExcel = async () => {
    if (!selectedBatch) return;
    try {
      const res = await salaryAPI.downloadBatchExcel(selectedBatch.id);
      downloadBlob(res.data, `Salary_${selectedBatch.month}.xlsx`);
      toast.success('Excel downloaded');
    } catch (error) {
      toast.error('Failed to download Excel');
    }
  };

  const openEditBatchModal = (record) => {
    setEditingBatchRecord(record);
    setEditBatchForm({
      basic_pay: record.basic_pay || 0, hra: record.hra || 0, da: record.da || 0,
      special_allowance: record.special_allowance || 0, conveyance: record.conveyance || 0,
      medical_allowance: record.medical_allowance || 0, other_allowances: record.other_allowances || 0,
      overtime: record.overtime || 0, bonus: record.bonus || 0, arrears: record.arrears || 0,
      pf_deduction: record.pf_deduction || 0, esi_deduction: record.esi_deduction || 0,
      tds_deduction: record.tds_deduction || 0, professional_tax: record.professional_tax || 0,
      leave_deduction: record.leave_deduction || 0, loan_deduction: record.loan_deduction || 0,
      other_deductions: record.other_deductions || 0, remarks: record.remarks || '',
    });
    setEditBatchModalOpen(true);
  };

  const handleUpdateBatchRecord = async () => {
    if (!editingBatchRecord || !selectedBatch) return;
    try {
      await salaryAPI.updateBatchRecord(selectedBatch.id, editingBatchRecord.id, editBatchForm);
      toast.success('Record updated');
      setEditBatchModalOpen(false);
      fetchBatchDetails(selectedBatch.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update record');
    }
  };

  const openBatchPayslip = async (record) => {
    try {
      const res = await salaryAPI.getBatchPayslip(selectedBatch.id, record.id);
      setSelectedPayslip(res.data);
      setPayslipModalOpen(true);
    } catch (error) {
      toast.error('Failed to load payslip');
    }
  };

  const downloadBatchPayslipPdf = async (record) => {
    try {
      const res = await salaryAPI.downloadPayslipPdf(selectedBatch.id, record.id);
      downloadBlob(res.data, `Payslip_${record.employee_code}_${selectedBatch.month}.pdf`);
      toast.success('Payslip downloaded');
    } catch (error) {
      toast.error('Failed to download payslip');
    }
  };

  const getBatchStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-slate-100 text-slate-700', label: 'Draft' },
      pending_authorization: { color: 'bg-amber-100 text-amber-700', label: 'Pending Authorization' },
      authorized: { color: 'bg-emerald-100 text-emerald-700', label: 'Authorized' },
      disbursed: { color: 'bg-blue-100 text-blue-700', label: 'Disbursed' },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const resetForm = () => ({
    employee_id: '', month: selectedMonth,
    basic_pay: 0, hra: 0, da: 0, special_allowance: 0, conveyance: 0, medical_allowance: 0,
    other_allowances: 0, overtime: 0, bonus: 0, arrears: 0,
    pf_deduction: 0, esi_deduction: 0, tds_deduction: 0, professional_tax: 0,
    leave_deduction: 0, loan_deduction: 0, advance_deduction: 0, other_deductions: 0,
    remarks: '',
  });

  const handleOpenModal = (record = null) => {
    if (record) {
      setSelectedRecord(record);
      setFormData({ ...resetForm(), ...record });
    } else {
      setSelectedRecord(null);
      setFormData(resetForm());
    }
    setIsModalOpen(true);
  };

  const handleViewSalaryDetail = (record) => {
    setSelectedDetailRecord(record);
    setIsDetailModalOpen(true);
  };

  const handleEmployeeSelect = (employeeId) => {
    const emp = employees.find((e) => e.id === employeeId);
    if (emp) {
      const basic = emp.basic_salary || 0;
      const pfDeduction = basic * 0.12;
      const gross = basic + (emp.hra || 0) + (emp.da || 0) + (emp.special_allowance || 0) + (emp.conveyance || 0) + (emp.medical_allowance || 0);
      const esiDeduction = gross <= 21000 ? gross * 0.0175 : 0;
      const tdsDeduction = gross * 0.1;

      setFormData({
        ...formData,
        employee_id: employeeId,
        basic_pay: basic,
        hra: emp.hra || 0, da: emp.da || 0,
        special_allowance: emp.special_allowance || 0, conveyance: emp.conveyance || 0,
        medical_allowance: emp.medical_allowance || 0,
        pf_deduction: Math.round(pfDeduction * 100) / 100,
        esi_deduction: Math.round(esiDeduction * 100) / 100,
        tds_deduction: Math.round(tdsDeduction * 100) / 100,
        professional_tax: 200,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedRecord) {
        await salaryAPI.update(selectedRecord.id, formData);
        toast.success('Salary record updated');
      } else {
        await salaryAPI.create(formData);
        toast.success('Salary record created');
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save salary record');
    }
  };

  const handleWorkflowAction = async (salaryId, action) => {
    try {
      await salaryAPI.workflow(salaryId, { action, remarks: `Salary ${action}` });
      toast.success(`Salary ${action} successful`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const handleBulkSubmit = async () => {
    try {
      const response = await salaryAPI.bulkSubmit(selectedMonth);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit salaries');
    }
  };

  const handleBulkDisburse = async () => {
    if (!window.confirm('Are you sure you want to disburse all approved salaries? This action cannot be undone.')) return;
    try {
      const response = await salaryAPI.bulkDisburse(selectedMonth);
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to disburse salaries');
    }
  };

  const handleGenerateExcel = async () => {
    try {
      const response = await salaryAPI.generateExcel(selectedMonth);
      downloadBlob(response.data, `salary_disbursement_${selectedMonth}.xlsx`);
      toast.success('Excel file downloaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate Excel');
    }
  };

  const handleViewAudit = async (employeeId) => {
    try {
      const response = await salaryAPI.getAudit(employeeId);
      setAuditLogs(response.data);
      setIsAuditModalOpen(true);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    }
  };

  const getEmployeeName = (employeeId) => employees.find((e) => e.id === employeeId)?.name || 'Unknown';
  const getEmployeeCode = (employeeId) => employees.find((e) => e.id === employeeId)?.employee_code || 'N/A';

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-slate-100 text-slate-700',
      pending_verification: 'bg-amber-100 text-amber-800',
      verified: 'bg-blue-100 text-blue-800',
      approved: 'bg-emerald-100 text-emerald-800',
      disbursed: 'bg-purple-100 text-purple-800',
    };
    return `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.draft}`;
  };

  const calculateTotals = () => {
    const gross = Number(formData.basic_pay) + Number(formData.hra) + Number(formData.da) +
      Number(formData.special_allowance) + Number(formData.conveyance) + Number(formData.medical_allowance) +
      Number(formData.other_allowances) + Number(formData.overtime) + Number(formData.bonus) + Number(formData.arrears);
    const deductions = Number(formData.pf_deduction) + Number(formData.esi_deduction) + Number(formData.tds_deduction) +
      Number(formData.professional_tax) + Number(formData.leave_deduction) + Number(formData.loan_deduction) +
      Number(formData.advance_deduction) + Number(formData.other_deductions);
    return { gross, deductions, net: gross - deductions };
  };

  const totals = calculateTotals();
  const draftCount = salaryRecords.filter(r => r.status === 'draft').length;
  const approvedCount = salaryRecords.filter(r => r.status === 'approved').length;

  const filteredRecords = salaryRecords.filter(record => {
    if (!searchTerm) return true;
    const empName = getEmployeeName(record.employee_id)?.toLowerCase() || '';
    const empCode = getEmployeeCode(record.employee_id)?.toLowerCase() || '';
    return empName.includes(searchTerm.toLowerCase()) || empCode.includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner w-8 h-8"></div>
      </div>
    );
  }

  return (
    <div data-testid="salary-management-page">
      <Header title="Salary Management" subtitle="Process and manage employee salaries" />

      <div className="p-8">
        {/* Main Tabs */}
        <Tabs value={mainTab} onValueChange={setMainTab} className="w-full mb-6">
          <TabsList>
            <TabsTrigger value="individual" className="gap-2">
              <Wallet className="w-4 h-4" />
              Individual Salaries
            </TabsTrigger>
            <TabsTrigger value="bulk" className="gap-2">
              <Banknote className="w-4 h-4" />
              Bulk Processing
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Individual Salaries Tab */}
        {mainTab === 'individual' && (
          <>
            {/* Actions Bar */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4">
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-48" data-testid="month-selector">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search employee..."
                    className="pl-9 w-48"
                    data-testid="salary-search-employee"
                  />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                {canEditSalary && draftCount > 0 && (
                  <Button variant="outline" onClick={handleBulkSubmit} data-testid="bulk-submit-btn">
                    <Send className="w-4 h-4 mr-2" />
                    Submit All for Verification ({draftCount})
                  </Button>
                )}
                {canApproveSalary && approvedCount > 0 && (
                  <Button variant="outline" onClick={handleBulkDisburse} data-testid="bulk-disburse-btn" className="text-purple-600 border-purple-200 hover:bg-purple-50">
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Disburse All ({approvedCount})
                  </Button>
                )}
                {canEditSalary && (
                  <Button onClick={() => handleOpenModal()} data-testid="add-salary-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Salary Record
                  </Button>
                )}
              </div>
            </div>

            <SalaryStatusSummary salaryRecords={salaryRecords} />
            <SalarySummaryCards salaryRecords={salaryRecords} formatCurrency={formatCurrency} />

            {/* Salary Table */}
            <div className="dashboard-card overflow-hidden">
              <table className="data-table" data-testid="salary-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Gross Salary</th>
                    <th>Deductions</th>
                    <th>Net Pay</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRecords.map((record) => (
                    <SalaryTableRow
                      key={record.id}
                      record={record}
                      getEmployeeName={getEmployeeName}
                      getEmployeeCode={getEmployeeCode}
                      formatCurrency={formatCurrency}
                      getStatusBadge={getStatusBadge}
                      canEditSalary={canEditSalary}
                      canApproveSalary={canApproveSalary}
                      isVerifier={isVerifier}
                      onEdit={handleOpenModal}
                      onViewDetail={handleViewSalaryDetail}
                      onWorkflowAction={handleWorkflowAction}
                      onViewAudit={handleViewAudit}
                    />
                  ))}
                </tbody>
              </table>

              {salaryRecords.length === 0 && (
                <div className="empty-state py-12">
                  <FileSpreadsheet className="w-12 h-12 text-slate-300" />
                  <h3>No salary records for {formatMonth(selectedMonth)}</h3>
                  <p>Create salary records for this month</p>
                </div>
              )}
            </div>
          </>
        )}

        {/* Bulk Processing Tab */}
        {mainTab === 'bulk' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Bulk Salary Processing</h2>
                <p className="text-sm text-slate-500">Generate salaries for all employees, verify and authorize</p>
              </div>
              <div className="flex items-center gap-3">
                <Button variant="outline" onClick={handleGenerateExcel} data-testid="bulk-download-excel-btn">
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  Download Excel
                </Button>
                <Button variant="outline" onClick={fetchBatches} disabled={loadingBatches} data-testid="refresh-batches-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${loadingBatches ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
                {canGenerate && (
                  <Button onClick={() => setGenerateModalOpen(true)} data-testid="generate-batch-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Generate Salary Batch
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Batches List */}
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Salary Batches</CardTitle>
                    <CardDescription>Select a batch to view details</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y max-h-[500px] overflow-y-auto">
                      {batches.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">
                          <Wallet className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                          <p>No salary batches yet</p>
                          {canGenerate && <p className="text-sm">Click "Generate Salary Batch" to create one</p>}
                        </div>
                      ) : (
                        batches.map((batch) => (
                          <div
                            key={batch.id}
                            className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors ${
                              selectedBatch?.id === batch.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                            }`}
                            onClick={() => fetchBatchDetails(batch.id)}
                            data-testid={`batch-item-${batch.id}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-slate-900">{batch.month}</span>
                              {getBatchStatusBadge(batch.status)}
                            </div>
                            <div className="text-sm text-slate-500">
                              <p>{batch.total_employees} employees</p>
                              <p className="font-medium text-slate-700">Net: {formatCurrency(batch.total_net)}</p>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Batch Details */}
              <div className="lg:col-span-2">
                {selectedBatch ? (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg flex items-center gap-2">
                            Salary Batch: {selectedBatch.month}
                            {getBatchStatusBadge(selectedBatch.status)}
                          </CardTitle>
                          <CardDescription>
                            Created by {selectedBatch.created_by_name} on {new Date(selectedBatch.created_at).toLocaleDateString()}
                          </CardDescription>
                        </div>
                        <div className="flex items-center gap-2">
                          {isAdmin && (
                            <Button variant="outline" onClick={handleDownloadBatchExcel} data-testid="download-batch-excel-btn">
                              <Download className="w-4 h-4 mr-2" />
                              Download Excel
                            </Button>
                          )}
                          {canGenerate && selectedBatch.status === 'draft' && (
                            <Button onClick={handleSubmitBatchForAuthorization} data-testid="submit-batch-btn">
                              <Send className="w-4 h-4 mr-2" />
                              Submit for Authorization
                            </Button>
                          )}
                          {canAuthorize && selectedBatch.status === 'pending_authorization' && (
                            <>
                              <Button variant="outline" onClick={() => setRejectModalOpen(true)} data-testid="reject-batch-btn">
                                <XCircle className="w-4 h-4 mr-2" />
                                Reject
                              </Button>
                              <Button onClick={handleAuthorizeBatch} className="bg-emerald-600 hover:bg-emerald-700" data-testid="authorize-batch-btn">
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Authorize
                              </Button>
                            </>
                          )}
                          {isAdmin && selectedBatch.status === 'draft' && (
                            <Button variant="destructive" onClick={handleDeleteBatch} data-testid="delete-batch-btn">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {/* Summary Cards */}
                      <div className="grid grid-cols-4 gap-4 mb-6">
                        <div className="bg-slate-50 rounded-lg p-4 text-center">
                          <Users className="w-6 h-6 mx-auto mb-1 text-slate-600" />
                          <p className="text-2xl font-bold text-slate-900">{selectedBatch.total_employees}</p>
                          <p className="text-xs text-slate-500">Employees</p>
                        </div>
                        <div className="bg-blue-50 rounded-lg p-4 text-center">
                          <Wallet className="w-6 h-6 mx-auto mb-1 text-blue-600" />
                          <p className="text-lg font-bold text-blue-900">{formatCurrency(selectedBatch.total_gross)}</p>
                          <p className="text-xs text-slate-500">Gross Salary</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4 text-center">
                          <AlertTriangle className="w-6 h-6 mx-auto mb-1 text-red-600" />
                          <p className="text-lg font-bold text-red-900">{formatCurrency(selectedBatch.total_deductions)}</p>
                          <p className="text-xs text-slate-500">Deductions</p>
                        </div>
                        <div className="bg-emerald-50 rounded-lg p-4 text-center">
                          <Check className="w-6 h-6 mx-auto mb-1 text-emerald-600" />
                          <p className="text-lg font-bold text-emerald-900">{formatCurrency(selectedBatch.total_net)}</p>
                          <p className="text-xs text-slate-500">Net Payable</p>
                        </div>
                      </div>

                      {selectedBatch.rejection_reason && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                          <p className="text-sm text-red-700">{selectedBatch.rejection_reason}</p>
                        </div>
                      )}

                      {/* Records Table */}
                      {loadingRecords ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="spinner w-6 h-6"></div>
                        </div>
                      ) : (
                        <div className="border rounded-lg overflow-hidden">
                          <div className="max-h-[350px] overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-100 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left">Employee</th>
                                  <th className="px-3 py-2 text-left">Code</th>
                                  <th className="px-3 py-2 text-right">Gross</th>
                                  <th className="px-3 py-2 text-right">Deductions</th>
                                  <th className="px-3 py-2 text-right">Net Payable</th>
                                  <th className="px-3 py-2 text-center">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y">
                                {batchRecords.map((record) => (
                                  <tr key={record.id} className="hover:bg-slate-50">
                                    <td className="px-3 py-2">
                                      <p className="font-medium text-slate-900">{record.employee_name}</p>
                                      <p className="text-xs text-slate-500">{record.department}</p>
                                    </td>
                                    <td className="px-3 py-2">
                                      <code className="text-xs bg-slate-100 px-1 rounded">{record.employee_code}</code>
                                    </td>
                                    <td className="px-3 py-2 text-right font-medium">{formatCurrency(record.gross_salary)}</td>
                                    <td className="px-3 py-2 text-right text-red-600">-{formatCurrency(record.total_deductions)}</td>
                                    <td className="px-3 py-2 text-right font-bold text-emerald-600">{formatCurrency(record.net_salary)}</td>
                                    <td className="px-3 py-2">
                                      <div className="flex items-center justify-center gap-1">
                                        <Button size="sm" variant="ghost" onClick={() => openBatchPayslip(record)} title="View Payslip">
                                          <Eye className="w-4 h-4" />
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => downloadBatchPayslipPdf(record)} title="Download PDF">
                                          <FileText className="w-4 h-4" />
                                        </Button>
                                        {canGenerate && selectedBatch.status === 'draft' && (
                                          <Button size="sm" variant="ghost" onClick={() => openEditBatchModal(record)} title="Edit">
                                            <Edit2 className="w-4 h-4" />
                                          </Button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <FileSpreadsheet className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                      <h3 className="text-lg font-semibold text-slate-900 mb-2">Select a Batch</h3>
                      <p className="text-slate-500">Click on a salary batch from the list to view details</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <SalaryFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedRecord={selectedRecord}
        formData={formData}
        setFormData={setFormData}
        onSubmit={handleSubmit}
        employees={employees}
        monthOptions={monthOptions}
        onEmployeeSelect={handleEmployeeSelect}
        totals={totals}
        formatCurrency={formatCurrency}
      />

      <SalaryDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        record={selectedDetailRecord}
        getEmployeeName={getEmployeeName}
        getEmployeeCode={getEmployeeCode}
        formatCurrency={formatCurrency}
        formatMonth={formatMonth}
        getStatusBadge={getStatusBadge}
      />

      <SalaryAuditModal
        isOpen={isAuditModalOpen}
        onClose={() => setIsAuditModalOpen(false)}
        auditLogs={auditLogs}
        formatMonth={formatMonth}
        formatCurrency={formatCurrency}
      />

      <GenerateBatchModal
        isOpen={generateModalOpen}
        onClose={() => setGenerateModalOpen(false)}
        bulkMonth={bulkMonth}
        setBulkMonth={setBulkMonth}
        bulkYear={bulkYear}
        setBulkYear={setBulkYear}
        bulkMonths={bulkMonths}
        bulkYears={bulkYears}
        generating={generating}
        onGenerate={handleGenerateBatch}
      />

      <EditBatchRecordModal
        isOpen={editBatchModalOpen}
        onClose={() => setEditBatchModalOpen(false)}
        editingRecord={editingBatchRecord}
        editForm={editBatchForm}
        setEditForm={setEditBatchForm}
        onSave={handleUpdateBatchRecord}
      />

      <RejectBatchModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        onReject={handleRejectBatch}
      />

      <BatchPayslipModal
        isOpen={payslipModalOpen}
        onClose={() => setPayslipModalOpen(false)}
        payslip={selectedPayslip}
        formatCurrency={formatCurrency}
        onDownload={downloadBatchPayslipPdf}
        selectedBatch={selectedBatch}
      />
    </div>
  );
};

export default SalaryManagement;
